import { ReactNode, createContext, useContext, useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import { useFormik, FormikProps } from "formik";
import { useSelector } from "react-redux";

import { RootState } from "@store/store";
import { useGetPermission } from "@hooks/useGetPermission";
import { PermissionV2 } from "@interfaces/users";
import { Condition, LoanApplicationQuoterDetails } from "@interfaces/application";
import { AgriculturalCosts } from "@interfaces/catalogs";
import { formatFullName, filterByNestedCodes } from "@helpers/generals";
import {
    useLazyGetApplicationQuery,
    useLazyGetConditionsQuery,
    usePutConditionMutation,
} from "@services/api/applications";
import {
    useLazyGetSowingTypeConfigQuery,
    useLazyGetPropertyLocationConfigQuery,
    useLazyGetAgriculturalCostsQuery,
} from "@services/api/catalogs";
import { useLazyGetPersonsQuery } from "@services/api/persons";

import {
    PROJECTION_YEARS_COUNT,
    ParametricosDataForm,
    ParametricosExtraConditions,
    ParametricosProjectionForm,
    defaultDataForm,
    defaultProjectionForm,
    resizeYearArray,
} from "../types";
import {
    ProjectionTable,
    buildProjectionTable,
    computeAmortizationSchedule,
    computeApalancamiento,
    computeContractedRate,
    computeCreditTermMonths,
    computeDefaultInteresCredito,
    computeDefaultInteresCreditoSimple,
    computeFinancingAmount,
    computeLiquidez,
    roundCreditAmount,
} from "../helpers/calculations";

interface ParametricosContextValue {
    loading: boolean;
    permissionData: PermissionV2 | undefined;
    permissionProjection: PermissionV2 | undefined;

    evaluationDate: string;
    evaluatorName: string;
    entityFederativaName: string;
    creditorName: string;
    sowingTypeName: string;

    hectares: number;
    yieldPerHectare: number;
    pricePerTon: number;
    creditQuotaPerHectare: number;
    costPerHectare: number;

    firstMinistrationDate: string;
    secondMinistrationDate: string;

    dataForm: FormikProps<ParametricosDataForm>;
    projectionForm: FormikProps<ParametricosProjectionForm>;

    creditAmount: number;
    financingAmount: number;
    contractMaturityDate: string;
    amortizationSchedule: ReturnType<typeof computeAmortizationSchedule>;
    interestType: string;
    contractedRate: number;
    creditTermMonths: number;
    creditMaturityDate: string;

    projectionTable: ProjectionTable;
    inversionTotalDelProyecto: number;
    capacidadDePago: number | null;
    liquidez: number;
    apalancamiento: number;

    save: () => Promise<void>;
}

const ParametricosContext = createContext<ParametricosContextValue | undefined>(undefined);

export const useParametricosContext = () => {
    const ctx = useContext(ParametricosContext);
    if (!ctx) throw new Error("useParametricosContext debe usarse dentro de <ParametricosProvider>");
    return ctx;
};

export const ParametricosProvider = ({ children }: { children: ReactNode }) => {
    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);
    const userSession = useSelector((state: RootState) => state.session.user);

    const permissionData = useGetPermission("SBPADAT");
    const permissionProjection = useGetPermission("SBPAPRO");

    const [loading, setLoading] = useState(true);
    const [condition, setCondition] = useState<Condition>();
    const [quoterDetails, setQuoterDetails] = useState<LoanApplicationQuoterDetails | null>(null);
    const [agriculturalCosts, setAgriculturalCosts] = useState<AgriculturalCosts>();
    const [sowingTypeName, setSowingTypeName] = useState("");
    const [entityFederativaName, setEntityFederativaName] = useState("");
    const [creditorName, setCreditorName] = useState("");

    const [triggerGetApplication] = useLazyGetApplicationQuery();
    const [triggerGetConditions] = useLazyGetConditionsQuery();
    const [triggerPutCondition] = usePutConditionMutation();
    const [triggerGetSowingTypeConfig] = useLazyGetSowingTypeConfigQuery();
    const [triggerGetPropertyLocationConfig] = useLazyGetPropertyLocationConfigQuery();
    const [triggerGetAgriculturalCosts] = useLazyGetAgriculturalCostsQuery();
    const [triggerGetPersons] = useLazyGetPersonsQuery();

    const evaluationDate = useMemo(() => dayjs().toISOString(), []);
    const evaluatorName = formatFullName(userSession);

    const dataForm = useFormik<ParametricosDataForm>({
        initialValues: defaultDataForm,
        onSubmit: async () => {},
    });

    const projectionForm = useFormik<ParametricosProjectionForm>({
        initialValues: defaultProjectionForm,
        onSubmit: async () => {},
    });

    const loadInformation = async () => {
        try {
            setLoading(true);

            const [conditions, application, persons] = await Promise.all([
                triggerGetConditions(
                    {
                        applicationId: applicationSelected.id,
                        params: new URLSearchParams([["q", "active:true"]]).toString(),
                    },
                    false,
                ).unwrap(),
                triggerGetApplication(applicationSelected.id, false).unwrap(),
                triggerGetPersons(
                    new URLSearchParams([["q", `loan_application_id:${applicationSelected.id}`]]).toString(),
                    false,
                ).unwrap(),
            ]);

            const activeCondition = conditions.at(0);
            setCondition(activeCondition);

            const quoter = application.quoter_details ?? null;
            setQuoterDetails(quoter);

            // const acreditado = filterByNestedCodes(persons, (person) => person.person_types_detail, ["PTAM"]).at(0);
            // setCreditorName(formatFullName(acreditado));
            setCreditorName(applicationSelected.comercial_name);

            let costs: AgriculturalCosts | undefined;
            if (quoter?.agricultural_costs_id) {
                const results = await triggerGetAgriculturalCosts(
                    new URLSearchParams([["q", `id:${quoter.agricultural_costs_id}`]]).toString(),
                ).unwrap();
                costs = results.at(0);
            }
            setAgriculturalCosts(costs);

            if (quoter?.sowing_type_id) {
                const sowingTypes = await triggerGetSowingTypeConfig(
                    new URLSearchParams([["q", `sowing_type_id:${quoter.sowing_type_id}`]]).toString(),
                ).unwrap();
                setSowingTypeName(sowingTypes.at(0)?.sowing_type.name ?? "");
            }

            if (quoter?.property_location_id) {
                const propertyLocations = await triggerGetPropertyLocationConfig(
                    new URLSearchParams([["q", `property_location_id:${quoter.property_location_id}`]]).toString(),
                ).unwrap();
                setEntityFederativaName(propertyLocations.at(0)?.property_location.name ?? "");
            }

            const parametric = (
                activeCondition?.extra_conditions as { parametric?: ParametricosExtraConditions } | null | undefined
            )?.parametric;

            const dataValues: ParametricosDataForm = {
                ...defaultDataForm,
                ...parametric?.data,
                contract: { ...defaultDataForm.contract, ...parametric?.data?.contract },
                liabilities: { ...defaultDataForm.liabilities, ...parametric?.data?.liabilities },
                assets: { ...defaultDataForm.assets, ...parametric?.data?.assets },
                marginRate: activeCondition?.extra_conditions?.margin_rate ?? 0,
            };
            dataForm.resetForm({ values: dataValues });

            const yearsCount = parametric?.projection?.servicesIncome?.length ?? PROJECTION_YEARS_COUNT;

            const interesCreditoSimple = parametric?.projection?.interesCreditoSimple
                ? resizeYearArray(parametric.projection.interesCreditoSimple, yearsCount)
                : computeDefaultInteresCreditoSimple(dataValues.longTermAmortizations, yearsCount);

            const seedContractedRate = computeContractedRate(
                activeCondition?.interest_rate ?? 0,
                activeCondition?.extra_conditions?.interest_type ?? "",
                dataValues.marginRate,
                activeCondition?.extra_conditions?.funding_rate ?? 0,
                activeCondition?.extra_conditions?.tiie_rate ?? 0,
            );
            const seedFinancingAmount = computeFinancingAmount(
                dataValues.assetValue,
                dataValues.financingAmountOverride,
            );
            const seedAmortizationSchedule = computeAmortizationSchedule(
                dataValues.amortizationRows,
                seedContractedRate / 100,
                evaluationDate,
                seedFinancingAmount,
            );
            const interesCredito = parametric?.projection?.interesCredito
                ? resizeYearArray(parametric.projection.interesCredito, yearsCount)
                : computeDefaultInteresCredito(seedAmortizationSchedule, yearsCount);

            projectionForm.resetForm({
                values: {
                    ...defaultProjectionForm,
                    ...parametric?.projection,
                    notes: Array.isArray(parametric?.projection?.notes)
                        ? parametric.projection.notes
                        : defaultProjectionForm.notes,
                    interesCreditoSimple,
                    interesCredito,
                },
            });
        } catch (error) {
            console.log("ParametricosContext - loadInformation:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (applicationSelected.id === "0-0-0-0-0") return;
        loadInformation();
    }, [applicationSelected.id]);

    const hectares = dataForm.values.hectaresOverride ?? quoterDetails?.hectares ?? 0;
    const yieldPerHectare = agriculturalCosts?.yield_per_hectare ?? 0;
    const pricePerTon = agriculturalCosts?.price_per_ton ?? 0;
    const creditQuotaPerHectare = agriculturalCosts?.credit_quota_per_hectare ?? 0;
    const costPerHectare =
        condition?.extra_conditions?.cost_per_hectare ?? agriculturalCosts?.credit_payment_per_hectare ?? 0;
    const authorizedRate = condition?.interest_rate ?? 0;
    const fundingRate = condition?.extra_conditions?.funding_rate ?? 0;
    const tiieRate = condition?.extra_conditions?.tiie_rate ?? 0;
    const interestType = condition?.extra_conditions?.interest_type ?? "";

    const ministrationsSorted = useMemo(
        () => [...(condition?.extra_conditions?.ministrations ?? [])].sort((a, b) => a.order - b.order),
        [condition],
    );
    const firstMinistrationDate = ministrationsSorted.at(0)?.date ?? "";
    const secondMinistrationDate = ministrationsSorted.at(1)?.date ?? "N/A";

    const creditAmount = roundCreditAmount(dataForm.values.creditSurfaceHa, creditQuotaPerHectare);
    const financingAmount = computeFinancingAmount(dataForm.values.assetValue, dataForm.values.financingAmountOverride);
    const contractedRate = computeContractedRate(
        authorizedRate,
        interestType,
        dataForm.values.marginRate,
        fundingRate,
        tiieRate,
    );
    const creditMaturityDate = condition?.extra_conditions?.maturity_date ?? agriculturalCosts?.maturity_date ?? "";
    const creditTermMonths = computeCreditTermMonths(creditMaturityDate, evaluationDate);

    const contractMaturityDate =
        dataForm.values.contract.maturityDateOverride ||
        (dataForm.values.contract.openingDate
            ? (dayjs(dataForm.values.contract.openingDate).add(1080, "day").toISOString() ?? "")
            : "");

    const amortizationSchedule = useMemo(
        () =>
            computeAmortizationSchedule(
                dataForm.values.amortizationRows,
                contractedRate / 100,
                evaluationDate,
                financingAmount,
            ),
        [dataForm.values.amortizationRows, contractedRate, evaluationDate, financingAmount],
    );

    const projectionTable = useMemo(
        () =>
            buildProjectionTable({
                hectares,
                yieldPerHectare,
                pricePerTon,
                costPerHectare,
                amortizationSchedule,
                longTermAmortizations: dataForm.values.longTermAmortizations,
                tasaContratada: contractedRate / 100,
                creditoAutorizado: creditAmount,
                plazoCreditoMeses: creditTermMonths,
                projection: projectionForm.values,
            }),
        [
            hectares,
            yieldPerHectare,
            pricePerTon,
            costPerHectare,
            amortizationSchedule,
            dataForm.values.longTermAmortizations,
            projectionForm.values,
            contractedRate,
            creditAmount,
            creditTermMonths,
        ],
    );

    const inversionTotalDelProyecto = projectionTable.costosProduccion[0] ?? 0;
    const capacidadDePago = projectionTable.capacidadDePago[0] ?? null;
    const liquidez = computeLiquidez(
        inversionTotalDelProyecto,
        creditAmount,
        amortizationSchedule.at(0)?.capital ?? 0,
        dataForm.values.liabilities.shortTerm,
        dataForm.values.longTermAmortizations[0],
    );
    const apalancamiento = computeApalancamiento(
        dataForm.values.liabilities.shortTerm + dataForm.values.liabilities.longTerm,
        creditAmount,
        financingAmount,
        inversionTotalDelProyecto,
        dataForm.values.assets.movable + dataForm.values.assets.immovable,
        dataForm.values.assetValue,
    );

    const save = async () => {
        const { marginRate, ...restDataFormValues } = dataForm.values;

        const updated = await triggerPutCondition({
            applicationId: applicationSelected.id,
            conditionId: condition?.id ?? "0-0-0-0-0",
            body: {
                extra_conditions: {
                    ...condition?.extra_conditions,
                    margin_rate: marginRate,
                    parametric: {
                        data: restDataFormValues,
                        projection: projectionForm.values,
                    },
                },
                user_id_modified: userSession.id,
                user_name: evaluatorName,
            },
        }).unwrap();

        setCondition(updated);
        dataForm.resetForm({ values: dataForm.values });
        projectionForm.resetForm({ values: projectionForm.values });
    };

    const value: ParametricosContextValue = {
        loading,
        permissionData,
        permissionProjection,
        evaluationDate,
        evaluatorName,
        entityFederativaName,
        creditorName,
        sowingTypeName,
        hectares,
        yieldPerHectare,
        pricePerTon,
        creditQuotaPerHectare,
        costPerHectare,
        firstMinistrationDate,
        secondMinistrationDate,
        dataForm,
        projectionForm,
        creditAmount,
        financingAmount,
        contractMaturityDate,
        amortizationSchedule,
        interestType,
        contractedRate,
        creditTermMonths,
        creditMaturityDate,
        projectionTable,
        inversionTotalDelProyecto,
        capacidadDePago,
        liquidez,
        apalancamiento,
        save,
    };

    return <ParametricosContext.Provider value={value}>{children}</ParametricosContext.Provider>;
};
